import {
  Badge,
  CloseButton,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
  useColorModeValue,
} from '@ecoinvent/ui';
import { BiCalendar, BiRuler } from 'react-icons/bi';
import { FiBox, FiPieChart } from 'react-icons/fi';
import { IoEarth } from 'react-icons/io5';
import { Link as RouterLink } from 'react-router';

import { activityTypes } from 'pages/Search/components/ProductSearchResultCard/ProductSearchResultTable/constants';
import { formatNumericalValue } from 'utilities/numbers';

import { CompareDataset } from './types';
import { useCompareDatasetDetails } from './useCompareDatasetDetails';

type CardProps = {
  dataset: CompareDataset;
  to?: string;
  onRemove?: () => void;
  isCompactOnMobile?: boolean;
};

const DetailLine = ({
  icon,
  isCompactOnMobile,
  children,
}: {
  icon: React.ElementType;
  isCompactOnMobile?: boolean;
  children: React.ReactNode;
}) => {
  const detailText = useColorModeValue('gray.700', 'gray.300');
  return (
    <HStack spacing={2} color={detailText} display={isCompactOnMobile ? { base: 'none', md: 'flex' } : 'flex'}>
      <Icon as={icon} boxSize={4} flexShrink={0} />
      <Text>{children}</Text>
    </HStack>
  );
};

export const CompareDatasetCard = ({ dataset: stagedDataset, to, onRemove, isCompactOnMobile }: CardProps) => {
  const dataset = useCompareDatasetDetails(stagedDataset);
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const hoverBorderColor = useColorModeValue('green.500', 'green.300');
  const grayText = useColorModeValue('gray.500', 'gray.400');
  const bg = useColorModeValue('white', 'whiteAlpha.100');

  return (
    <LinkBox
      flex={1}
      minW={0}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      bg={bg}
      p={{ base: 4, md: 6 }}
      _hover={to ? { borderColor: hoverBorderColor } : undefined}
    >
      {onRemove && (
        <CloseButton
          position="absolute"
          top={3}
          right={3}
          size="sm"
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          onClick={onRemove}
          aria-label="Remove dataset from comparison"
        />
      )}
      <Flex wrap="wrap" align="center" justify="space-between" columnGap={4} rowGap={4} pr={onRemove ? 10 : 0}>
        <Stack spacing={1} flex="1 1 190px" minW={0}>
          <Text fontSize="sm" color={grayText}>
            {dataset.version}-{dataset.systemModel}
          </Text>
          <Heading size="sm">
            {to ? (
              <LinkOverlay as={RouterLink} to={to}>
                {dataset.activityName}
              </LinkOverlay>
            ) : (
              dataset.activityName
            )}
          </Heading>
          <Text>{dataset.referenceProduct}</Text>
          <HStack spacing={2} pt={1} flexWrap="wrap">
            {dataset.activityType && (
              <Badge variant="subtle" colorScheme="green" display="flex" alignItems="center" gap={1} textTransform="uppercase">
                <Icon as={FiBox} />
                {activityTypes[dataset.activityType]}
              </Badge>
            )}
            <Badge variant="solid" colorScheme="green">
              Activity
            </Badge>
          </HStack>
        </Stack>
        <Stack spacing={2} flex="0 1 220px" minW={0}>
          {typeof dataset.impactScore === 'number' && (
            <>
              <HStack spacing={1}>
                <Text color={grayText} textTransform="uppercase">
                  Impact:
                </Text>
                <Text fontWeight="bold">
                  {formatNumericalValue(dataset.impactScore)} {dataset.impactUnit}
                </Text>
              </HStack>
              <Divider borderColor={borderColor} />
            </>
          )}
          <DetailLine icon={IoEarth}>{dataset.geography}</DetailLine>
          {dataset.sector && (
            <DetailLine icon={FiPieChart} isCompactOnMobile={isCompactOnMobile}>
              {dataset.sector}
            </DetailLine>
          )}
          {dataset.timePeriod && (
            <DetailLine icon={BiCalendar} isCompactOnMobile={isCompactOnMobile}>
              {dataset.timePeriod}
            </DetailLine>
          )}
          <DetailLine icon={BiRuler} isCompactOnMobile={isCompactOnMobile}>
            {dataset.unit}
          </DetailLine>
        </Stack>
      </Flex>
    </LinkBox>
  );
};

export const CompareDatasetPlaceholder = ({ index }: { index: number }) => {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const grayText = useColorModeValue('gray.500', 'gray.500');

  return (
    <Stack
      flex={1}
      minW={0}
      borderWidth="1px"
      borderColor={borderColor}
      borderStyle="dashed"
      borderRadius="lg"
      p={{ base: 4, md: 6 }}
      align="center"
      justify="center"
      spacing={1}
    >
      <Text fontWeight="semibold" color={grayText}>
        Dataset {index}
      </Text>
      <Text fontSize="sm" color={grayText}>
        add dataset to compare
      </Text>
    </Stack>
  );
};
